import { z } from 'zod';
import { PipelineDefinitionSchema, TriggerExprSchema } from '@wit-support/pipeline';
// Re-exported so the ADMIN layers (UI included) reach the engine's canonical
// error-status set and its step-cause extractor without depending on the engine
// package directly — `describeStepFailure` is the same function the worker uses
// to compose `pipeline_run.error`, so the rail and the DB row can never disagree
// on what the cause of a step was.
export { PIPELINE_ERROR_STEP_STATUSES, isErrorStepStatus, describeStepFailure, } from '@wit-support/pipeline';
/**
 * Admin-facing Zod schemas + DTOs for the pipeline CRUD + executions surface.
 *
 * The node-graph `definition` is NOT redefined here — it is imported from
 * `@wit-support/pipeline` (`PipelineDefinitionSchema`), the single source of
 * truth shared by the engine, the db store, and this admin layer. These
 * contracts wrap that schema with the persistence/transport shape (ids,
 * timestamps, enabled flag, run/step views).
 */
/**
 * RUN status → Russian phrase. THE source of truth for this vocabulary: the
 * inbox rail, the pipeline-runs page and the Telegram card all read it, so a new
 * run status is named once.
 *
 * It lives HERE rather than in `@wit-support/ui-domain` (where the other RU
 * dictionaries sit) for one reason: `ui-domain` is a React package, and the
 * Telegram bot is a Node process that must not pull React in to print a line of
 * text. `admin-contracts` is the widest React-free package both already depend
 * on. Badge TONE stays in `ui-domain` — that is a UI concern with no meaning on
 * a Telegram card.
 *
 * Stored lowercase because that is the form with the most call sites: the card
 * renders it mid-sentence («⚙️ Пайплайн: Идентификация — ждём ответа клиента»).
 * Admin badges capitalize via `pipelineRunStatusLabelRu` in `ui-domain`.
 *
 * Deliberately keyed by plain `string`, like the step-status maps: it is also
 * fed historical `pipeline_run.status` values read back from the DB.
 */
export const PIPELINE_RUN_STATUS_RU = {
    running: 'выполняется',
    // The one status that is not about the BOT's progress — it says WHO the run is
    // blocked on, because that is the only part an operator can act on. Any node
    // that suspends a run lands here, not just today's `collect_input`.
    waiting: 'ждём ответа клиента',
    completed: 'завершён',
    failed: 'ошибка',
    // Terminal, and NOT a failure: the only writer is the merge engine, which
    // cancels a source's duplicate active run so two runs of the same pipeline
    // don't walk one conversation. The reason rides in the label because
    // «отменён» alone would read as «кто-то отменил» — nobody did; a merge did.
    cancelled: 'отменён (слияние)',
};
/**
 * Safe lookup of a run status's Russian phrase. An unknown status is returned
 * VERBATIM — machine values are never translated or prettified, the same
 * `?? raw` idiom the gate-reason and conversation-status lookups use. Callers
 * can therefore detect "not translated" by identity with the input.
 */
export function pipelineRunStatusRu(status) {
    return PIPELINE_RUN_STATUS_RU[status] ?? status;
}
export const PipelineId = z.guid().brand();
export const PipelineRunId = z.guid().brand();
/** Allowed characters of a pipeline key — shared with the editor's inline check. */
export const PIPELINE_KEY_PATTERN = /^[a-z0-9_]+$/;
export const PIPELINE_KEY_MIN_LENGTH = 2;
/** Slug-ish stable key referenced by the classifier link (Task 4). */
export const PipelineKey = z
    .string()
    .regex(PIPELINE_KEY_PATTERN, 'lowercase letters, digits, underscore')
    .min(PIPELINE_KEY_MIN_LENGTH);
export const PipelineDTO = z.object({
    id: PipelineId,
    key: z.string(),
    name: z.string(),
    enabled: z.boolean(),
    definition: PipelineDefinitionSchema,
    trigger: TriggerExprSchema.nullable(),
    version: z.number().int(),
    runCount: z.number().int(),
    /**
     * Runs started in the last 30 days (by `pipeline_run.created_at`) — the list
     * card's activity figure. `nullable().default(null)` per the rollout
     * invariant: the new bundle must render against an admin-api that does not
     * send it yet, and `GET /:id` / write responses leave it `null` (only the list
     * pays for the aggregate). Readers guard with `!= null`.
     */
    runCount30d: z.number().int().nullable().default(null),
    /** Runs currently parked in `waiting` (any age). Same rollout invariant. */
    waitingCount: z.number().int().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Bounds of the `days` window of `GET /api/pipelines/:id/step-stats`. */
export const PIPELINE_STEP_STATS_MAX_DAYS = 365;
export const PIPELINE_STEP_STATS_DEFAULT_DAYS = 30;
/**
 * Query of `GET /api/pipelines/:id/step-stats`. Strict integer 1..365 — a typo
 * 400s instead of silently meaning «30».
 */
export const PipelineStepStatsQuery = z.object({
    days: z.coerce
        .number()
        .int()
        .min(1)
        .max(PIPELINE_STEP_STATS_MAX_DAYS)
        .default(PIPELINE_STEP_STATS_DEFAULT_DAYS),
});
/**
 * Synthetic step ids the resume path writes BEFORE the resumed walk (they are not
 * nodes of any graph). Mirrors `OPERATOR_TAKEOVER_NODE_ID` and
 * `CROSS_CONVERSATION_RESUME_NODE_ID` in `apps/worker/src/handlers/pipeline-resume.ts`
 * — the worker's resume-facts e2e test pins the two spellings against each other.
 */
export const PIPELINE_RESUME_MARKER_NODE_IDS = {
    operatorTakeover: 'operator_takeover',
    crossConversation: 'resumed_from_other_conversation',
};
/** How a park ended, as far as the stored rows can tell. */
export const PIPELINE_RESUMED_OUTCOMES = ['collected', 'escalated', 'unknown'];
/**
 * Per-node traffic of one pipeline, for numbers on the builder's arrows.
 *
 * - `steps` — `pipeline_run_step` rows of runs CREATED in the window, grouped by
 *   `(nodeId, status)`. `status` is the raw step status (`ok`, `branch_true`,
 *   `waiting`, `outcome_*`, …); the client maps it onto the node's exits. A
 *   `nodeId` that is not in the current definition (a deleted node, or a
 *   synthetic audit marker the resume path writes) is passed through — the
 *   client ignores what it cannot place.
 * - `waiting` — runs parked in `waiting` RIGHT NOW (not limited by `days`),
 *   grouped by the node they wait on (`pipeline_run.resume_node_id`, which the
 *   park writes as the `collect_input` node itself).
 * - `resumed` — parks that ENDED (reply, timeout, operator takeover, or a resume
 *   down a `null` target that finished the run on the spot) for runs created in
 *   the window, by wait node and reconstructed outcome (admin-core
 *   `classifyResumedPark`). The resume writes no `outcome_*` step for the wait
 *   node, so these exits are invisible in `steps`; the client adds them to the
 *   wait node's `collected`/`escalated` exits. `parkStepKept` counts the runs of
 *   the item whose `(nodeId, 'waiting')` step is still in `steps` (the
 *   null-target finish leaves the audit as it was) — the client must not count
 *   their passage through the wait node a second time. A precollect short-circuit
 *   (no park) IS a regular `outcome_collected` step and never appears here, so
 *   the two never double-count. Only the run's LAST park is visible: a run
 *   re-parked at a chained `collect_input` keeps the snapshot of the newest park
 *   only.
 * - `truncatedBeforeResume` — runs in the window whose step audit was REWRITTEN
 *   by a resume (`clearRunSteps`; not the null-target finish, which keeps it):
 *   every step up to and including the park is gone, so `steps` under-counts the nodes before the wait for these runs. A
 *   lower bound — a run that resumed and then re-parked is not detectable as
 *   truncated until its newest park ends.
 *
 * - `continuations` — where the runs parked RIGHT NOW will continue: the targets
 *   of their `awaiting.outcomes` snapshots (taken at park time, so possibly from
 *   an older version of the graph), with distinct parked runs per target. The
 *   very set the save guard protects besides the wait nodes (`409
 *   pipeline_nodes_in_use`, admin-core `collectNodesInUse`) — the client must
 *   not re-derive it from the current graph alone.
 *
 * `resumed`/`truncatedBeforeResume` default to empty/0 so a client parsing an
 * older admin-api response keeps working. `continuations` is optional: absent
 * = not reported (an older admin-api — the client derives the continuations
 * from the parked steps' exits in the saved graph); present, even empty = the
 * server's word, used alone.
 */
export const PipelineStepStatsDTO = z.object({
    days: z.number().int(),
    totalRuns: z.number().int(),
    steps: z.array(z.object({ nodeId: z.string(), status: z.string(), count: z.number().int() })),
    waiting: z.array(z.object({ nodeId: z.string(), count: z.number().int() })),
    resumed: z
        .array(z.object({
        nodeId: z.string(),
        outcome: z.enum(PIPELINE_RESUMED_OUTCOMES),
        count: z.number().int(),
        /**
         * Of `count`, the runs whose park step `(nodeId, 'waiting')` is still in
         * `steps` (a resume down a `null` target finishes without rewriting the
         * audit) — their passage through the wait node is ALREADY in `steps`.
         */
        parkStepKept: z.number().int().default(0),
    }))
        .default([]),
    truncatedBeforeResume: z.number().int().default(0),
    // Optional, NOT `.default([])`: «the server did not report» (an older
    // admin-api — the client then derives continuations from the graph) must
    // stay distinguishable from «reported, none».
    continuations: z.array(z.object({ nodeId: z.string(), runCount: z.number().int() })).optional(),
    /**
     * Runs parked RIGHT NOW, all of them — also those with no wait node recorded
     * or whose wait node is gone (absent from `waiting`), and those only
     * `continuations` show. «Есть ли ждущие» is this, never `waiting` alone.
     * Absent from an older admin-api.
     */
    waitingRunTotal: z.number().int().optional(),
});
/**
 * Body of the `409` a `PUT`/`PATCH` gets when the new `definition` drops a node
 * that a parked run still needs (its wait node or a continuation target from
 * its `awaiting` snapshot). `runCount` = distinct parked runs needing the node.
 */
export const PipelineNodesInUseErrorDTO = z.object({
    error: z.literal('pipeline_nodes_in_use'),
    message: z.string(),
    nodes: z.array(z.object({
        nodeId: z.string(),
        runCount: z.number().int(),
        /**
         * `parked` — runs wait ON the step; `continuation` — only a target of
         * their snapshots. Absent from an older admin-api (treat as `parked`).
         */
        kind: z.enum(['parked', 'continuation']).optional(),
        /**
         * The two roles counted apart — one node may be the wait step of some
         * runs and the continuation of others (`kind` then says `parked`).
         * Absent from an older admin-api: the client falls back to `kind` +
         * `runCount`.
         */
        parkedRunCount: z.number().int().optional(),
        continuationRunCount: z.number().int().optional(),
        /**
         * What the save does to it: `removed`, or `retyped` (kept with another
         * type — the resume would run a different kind of step). Absent from an
         * older admin-api, which refuses removals only.
         */
        reason: z.enum(['removed', 'retyped']).default('removed'),
    })),
});
/**
 * Body of the `409` a `DELETE /api/pipelines/:id` gets while runs of the
 * pipeline are parked (`count`), unless it carries `?force=true`: deleting it
 * makes every one of them fail on resume and land back with an operator. The
 * client asks the author to confirm that, then retries with `force`.
 */
export const PipelineHasWaitingRunsErrorDTO = z.object({
    error: z.literal('pipeline_has_waiting_runs'),
    message: z.string(),
    count: z.number().int(),
});
/**
 * Body of the `409` a `PUT`/`PATCH` gets when it carries `expectedVersion` and
 * the stored `pipeline.version` has moved on (someone else saved in between).
 * `currentVersion` is the version now in the DB — the client reloads before
 * retrying rather than blindly resending with it, which would overwrite the
 * other edit after all.
 */
export const PipelineModifiedErrorDTO = z.object({
    error: z.literal('pipeline_modified'),
    message: z.string(),
    currentVersion: z.number().int(),
});
export const CreatePipelineRequest = z.object({
    key: PipelineKey.meta({ label: 'Ключ' }),
    name: z.string().min(1).meta({ label: 'Название' }),
    enabled: z.boolean().optional().meta({ label: 'Включён' }),
    /** Validated against the engine's node-graph schema. */
    definition: PipelineDefinitionSchema,
    trigger: TriggerExprSchema.nullable().optional().meta({ label: 'Условие запуска' }),
});
export const UpdatePipelineRequest = z
    .object({
    name: z.string().min(1).optional().meta({ label: 'Название' }),
    enabled: z.boolean().optional().meta({ label: 'Включён' }),
    definition: PipelineDefinitionSchema.optional(),
    trigger: TriggerExprSchema.nullable().optional(),
    /**
     * Optimistic lock: the `version` the editor loaded. When set and not equal
     * to the stored version the save is refused with `409 pipeline_modified`;
     * omitted = last write wins (the pre-lock behaviour, kept for old clients).
     * Not a change by itself, so it does not count toward the refine below.
     */
    expectedVersion: z.number().int().min(1).optional(),
})
    .refine((v) => v.name !== undefined ||
    v.enabled !== undefined ||
    v.definition !== undefined ||
    v.trigger !== undefined, {
    message: 'At least one of name, enabled, definition, or trigger must be provided',
});
/** Per-step row of an execution timeline. */
export const PipelineRunStepDTO = z.object({
    nodeId: z.string(),
    kind: z.string(),
    type: z.string(),
    /** 'ok' | 'branch_true' | 'branch_false' | 'skipped' | 'failed'. */
    status: z.string(),
    result: z.unknown().nullable(),
    seq: z.number().int(),
});
export const PipelineRunDTO = z.object({
    id: PipelineRunId,
    pipelineId: PipelineId,
    conversationId: z.string(),
    /** 'running' | 'completed' | 'failed'. */
    status: z.string(),
    error: z.string().nullable(),
    createdAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
});
export const PipelineRunListResponse = z.object({
    items: z.array(PipelineRunDTO),
});
/** A single run plus its ordered step timeline (detail view). */
export const PipelineRunDetailDTO = PipelineRunDTO.extend({
    steps: z.array(PipelineRunStepDTO),
    /**
     * The wait node of the run's LAST `collect_input` park
     * (`pipeline_run.resume_node_id`), `null` for a run that never parked. A
     * resume rewrites the step audit, so a run with a non-null value may be
     * missing every step before the wait.
     */
    resumeNodeId: z.string().nullable().default(null),
    /**
     * How that park ended (reconstructed from the stored rows, see admin-core
     * `classifyResumedPark`); `null` while the run still waits, for a park that
     * did not end as a resume (cancelled by a merge), and for a run that never
     * parked. Both fields are `nullable().default(null)` — the rollout invariant:
     * the new bundle must render against an admin-api that does not send them.
     */
    resumedOutcome: z.enum(PIPELINE_RESUMED_OUTCOMES).nullable().default(null),
});
//# sourceMappingURL=pipelines.js.map